import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, X } from "lucide-react";
import { warn } from "@/lib/logger";
import { useCompanyInfo } from "@/lib/company";

const STORAGE_KEY = "solarsim_install_dismissed";

export default function InstallPrompt() {
  const { t } = useTranslation();
  const company = useCompanyInfo();
  const brand = company?.brand_name || "SolarSim";
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch (err) {
      warn("InstallPrompt: localStorage unavailable", err);
      return;
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (!visible || !deferred) return null;

  const install = async () => {
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome) {
        try {
          localStorage.setItem(STORAGE_KEY, "1");
        } catch (err) {
          warn("InstallPrompt.install: localStorage unavailable", err);
        }
      }
    } finally {
      setVisible(false);
      setDeferred(null);
    }
  };

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (err) {
      warn("InstallPrompt.dismiss: localStorage unavailable", err);
    }
    setVisible(false);
    setDeferred(null);
  };

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed inset-x-4 bottom-4 z-50 mx-auto flex max-w-md items-center justify-between gap-3 rounded-2xl border border-solar-green/30 bg-white px-4 py-3 shadow-2xl shadow-solar-green/10 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:left-auto"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-solar-green/10 text-solar-green">
          <Download className="h-4 w-4" strokeWidth={2.4} />
        </span>
        <div>
          <div className="text-sm font-semibold text-solar-slate">
            {t("pwa.title", { brand })}
          </div>
          <div className="text-xs text-slate-500">{t("pwa.subtitle", { brand })}</div>
        </div>
      </div>
      <div className="flex flex-none items-center gap-2">
        <button
          type="button"
          data-testid="pwa-install-later"
          onClick={dismiss}
          aria-label={t("pwa.later")}
          className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-solar-slate"
        >
          <X className="h-4 w-4" />
        </button>
        <button
          type="button"
          data-testid="pwa-install-btn"
          onClick={install}
          className="inline-flex items-center gap-1 rounded-full bg-solar-green px-3.5 py-1.5 text-xs font-semibold text-white shadow hover:bg-solar-green-dark"
        >
          {t("pwa.install")}
        </button>
      </div>
    </div>
  );
}
